/**
 * APX WorkCenter — Email Inbox (Two-Panel Split)
 * Left panel (40%): email list with search, filters, sync
 * Right panel (60%): full email reading pane with body, attachments, reply
 * Role-filtered: Bkkprs see only their assigned clients; all others see full inbox.
 */

import { useState, useCallback, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ─── Tag colours ─────────────────────────────────────────────────────────────
const TAG_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  client:   { bg: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)",  border: "oklch(0.92 0.005 240 / 30%)" },
  document: { bg: "oklch(0.92 0.005 240 / 15%)",  color: "oklch(0.08 0.01 240)",  border: "oklch(0.92 0.005 240 / 30%)"  },
  tax:      { bg: "oklch(0.92 0.005 240 / 15%)",  color: "oklch(0.08 0.01 240)",  border: "oklch(0.92 0.005 240 / 30%)"  },
  invoice:  { bg: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)",  border: "oklch(0.92 0.005 240 / 30%)" },
  payroll:  { bg: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)",  border: "oklch(0.92 0.005 240 / 30%)" },
  general:  { bg: "oklch(0.5 0.02 240 / 15%)",  color: "oklch(0.14 0.04 240)", border: "oklch(0.5 0.02 240 / 25%)"  },
};

type EmailAttachment = {
  filename: string;
  mimeType: string;
  size: number;
  s3Url: string;
  s3Key: string;
  isImage: boolean;
  isPdf: boolean;
};

type EmailItem = {
  uid: number;
  messageId: string;
  from: string;
  fromEmail: string;
  to: string;
  subject: string;
  preview: string;
  body: string;
  htmlBody?: string;
  date: number;
  timeAgo: string;
  unread: boolean;
  tag: "client" | "document" | "tax" | "invoice" | "payroll" | "general";
  attachments: EmailAttachment[];
  hasAttachments: boolean;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Compose Modal ────────────────────────────────────────────────────────────
function ComposeModal({
  onClose,
  staffName,
  staffEmail,
  staffRole,
  clientEmails,
}: {
  onClose: () => void;
  staffName: string;
  staffEmail: string;
  staffRole: string;
  clientEmails: { name: string; email: string | null }[];
}) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [clientName, setClientName] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const composeMutation = trpc.email.compose.useMutation({
    onSuccess: () => { toast.success("Email sent successfully"); onClose(); },
    onError: (err) => { toast.error(`Failed to send: ${err.message}`); },
  });

  const suggestions = useMemo(() => {
    if (!to || to.length < 2) return [];
    const lower = to.toLowerCase();
    return clientEmails
      .filter((c) => c.email && (c.name.toLowerCase().includes(lower) || (c.email ?? "").toLowerCase().includes(lower)))
      .slice(0, 6);
  }, [to, clientEmails]);

  const handleSend = () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Please fill in To, Subject, and Body");
      return;
    }
    composeMutation.mutate({ toEmail: to.trim(), toName: clientName || to.trim(), subject: subject.trim(), body: body.trim(), clientName: clientName || undefined, staffName, staffEmail, staffRole });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "oklch(0 0 0 / 70%)" }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col"
        style={{ background: "oklch(0.97 0.005 85)", border: "1px solid oklch(0 0 0 / 12%)", maxHeight: "90vh" }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid oklch(0 0 0 / 12%)" }}>
          <h2 className="text-base font-bold" style={{ color: "oklch(0.12 0.04 240)", fontFamily: "'Playfair Display', serif" }}>New Email</h2>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5" style={{ color: "oklch(0.14 0.04 240)" }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3">
          <div className="relative">
            <label className="block text-sm font-semibold mb-1" style={{ color: "oklch(0.14 0.04 240)" }}>TO</label>
            <input type="email" value={to} onChange={(e) => { setTo(e.target.value); setShowSuggestions(true); }} onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="client@email.com" className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(0 0 0 / 12%)", color: "oklch(0.12 0.04 240)" }} />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 rounded-lg overflow-hidden shadow-xl"
                style={{ background: "oklch(1 0 0)", border: "1px solid oklch(0 0 0 / 12%)" }}>
                {suggestions.map((s) => (
                  <button key={s.email} className="w-full px-3 py-2 text-left text-sm hover:bg-black/5 flex items-center gap-2"
                    style={{ color: "oklch(0.12 0.04 240)" }}
                    onMouseDown={() => { setTo(s.email ?? ""); setClientName(s.name); setShowSuggestions(false); }}>
                    <span className="font-medium" style={{ color: "oklch(0.08 0.01 240)" }}>{s.name}</span>
                    <span style={{ color: "oklch(0.35 0.02 240)" }}>{s.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: "oklch(0.14 0.04 240)" }}>SUBJECT</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(0 0 0 / 12%)", color: "oklch(0.12 0.04 240)" }} />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-semibold mb-1" style={{ color: "oklch(0.14 0.04 240)" }}>MESSAGE</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message…" rows={8}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
              style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(0 0 0 / 12%)", color: "oklch(0.12 0.04 240)" }} />
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: "1px solid oklch(0 0 0 / 12%)" }}>
          <span className="text-sm" style={{ color: "oklch(0.35 0.02 240)" }}>From: apxadvisor@gmail.com</span>
          <button onClick={handleSend} disabled={composeMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, oklch(0.72 0.14 75), oklch(0.6 0.12 75))", color: "oklch(0.12 0.04 240)" }}>
            {composeMutation.isPending ? "Sending…" : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Email List Row (left panel) ──────────────────────────────────────────────
function EmailListRow({
  email,
  isSelected,
  onSelect,
}: {
  email: EmailItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const tagStyle = TAG_COLORS[email.tag] ?? TAG_COLORS.general;
  return (
    <button
      onClick={onSelect}
      className="w-full text-left px-4 py-3 transition-all duration-100 flex flex-col gap-1"
      style={{
        background: isSelected
          ? "oklch(0.90 0.02 220)"
          : email.unread
          ? "oklch(0.97 0.005 85)"
          : "oklch(1 0 0)",
        borderBottom: "1px solid oklch(0 0 0 / 8%)",
        borderLeft: isSelected ? "3px solid oklch(0.72 0.14 75)" : "3px solid transparent",
      }}
    >
      {/* Row 1: sender + time */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {email.unread && (
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "oklch(0.72 0.14 75)" }} />
          )}
          <span
            className="text-base font-semibold truncate"
            style={{ color: email.unread ? "oklch(0.08 0.01 240)" : "oklch(0.22 0.03 240)" }}
          >
            {email.from}
          </span>
          <span
            className="text-sm px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 capitalize"
            style={{ background: tagStyle.bg, color: tagStyle.color, border: `1px solid ${tagStyle.border}` }}
          >
            {email.tag}
          </span>
          {email.hasAttachments && (
            <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "oklch(0.35 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
            </svg>
          )}
        </div>
        <span className="text-sm flex-shrink-0" style={{ color: "oklch(0.35 0.02 240)" }}>{email.timeAgo}</span>
      </div>
      {/* Row 2: subject */}
      <p className="text-base font-medium truncate" style={{ color: email.unread ? "oklch(0.12 0.04 240)" : "oklch(0.22 0.03 240)" }}>
        {email.subject}
      </p>
      {/* Row 3: preview */}
      <p className="text-sm truncate" style={{ color: "oklch(0.38 0.02 240)" }}>{email.preview}</p>
    </button>
  );
}

// ─── Reading Pane (right panel) ───────────────────────────────────────────────
function ReadingPane({
  email,
  isReadOnly,
  staffName,
  staffEmail,
  staffRole,
}: {
  email: EmailItem | null;
  isReadOnly: boolean;
  staffName: string;
  staffEmail: string;
  staffRole: string;
}) {
  const [replyText, setReplyText] = useState("");
  const [showReply, setShowReply] = useState(false);
  const utils = trpc.useUtils();

  const replyMutation = trpc.email.reply.useMutation({
    onSuccess: () => {
      toast.success("Reply sent");
      setReplyText("");
      setShowReply(false);
      utils.email.list.invalidate();
    },
    onError: (err) => toast.error(`Failed to send reply: ${err.message}`),
  });

  const handleReply = () => {
    if (!email || !replyText.trim()) return;
    replyMutation.mutate({
      toEmail: email.fromEmail,
      toName: email.from,
      subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      replyText: replyText.trim(),
      originalMessageId: email.messageId,
      staffName,
      staffEmail,
      staffRole,
    });
  };

  if (!email) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3" style={{ background: "oklch(0.97 0.005 85)" }}>
        <svg className="w-12 h-12" style={{ color: "oklch(0.75 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
        </svg>
        <p className="text-sm" style={{ color: "oklch(0.45 0.02 240)" }}>Select an email to read</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: "oklch(0.97 0.005 85)" }}>
      {/* Reading pane header */}
      <div className="px-5 py-2.5 flex-shrink-0" style={{ borderBottom: "1px solid oklch(0 0 0 / 10%)", background: "oklch(1 0 0)" }}>
        <p className="text-sm font-semibold truncate mb-1" style={{ color: "oklch(0.10 0.04 240)" }}>
          {email.subject}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs" style={{ color: "oklch(0.35 0.02 240)" }}>
          <span><span className="font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>From:</span> {email.from} &lt;{email.fromEmail}&gt;</span>
          <span><span className="font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>To:</span> {email.to}</span>
          <span><span className="font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>Date:</span> {new Date(email.date).toLocaleString()}</span>
        </div>
      </div>

      {/* Scrollable body area */}
      <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5">
        {/* Email body — letter-width, centered */}
        <div className="mx-auto" style={{ maxWidth: "620px" }}>
          <div
            className="rounded-xl p-5 text-base leading-relaxed mb-5"
            style={{ background: "oklch(1 0 0)", color: "oklch(0.12 0.04 240)", whiteSpace: "pre-wrap", border: "1px solid oklch(0 0 0 / 8%)", wordBreak: "break-word", overflowWrap: "break-word" }}
          >
            {email.body || "(No body content)"}
          </div>

          {/* Attachments */}
          {email.hasAttachments && (
            <div className="mb-5">
              <p className="text-sm font-semibold mb-2" style={{ color: "oklch(0.14 0.04 240)" }}>
                ATTACHMENTS ({email.attachments.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {email.attachments.map((att) => (
                  <a
                    key={att.s3Key}
                    href={att.s3Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={att.filename}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium hover:opacity-80 transition-opacity"
                    style={{ background: "oklch(1 0 0)", border: "1px solid oklch(0 0 0 / 12%)", color: "oklch(0.14 0.04 240)" }}
                  >
                    {att.isPdf ? (
                      <svg className="w-4 h-4 flex-shrink-0" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 flex-shrink-0" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    )}
                    <span className="max-w-[180px] truncate">{att.filename}</span>
                    <span style={{ color: "oklch(0.35 0.02 240)" }}>{formatBytes(att.size)}</span>
                    <svg className="w-3 h-3 flex-shrink-0" style={{ color: "oklch(0.35 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reply area — always visible at bottom */}
      {!isReadOnly && (
        <div className="flex-shrink-0 px-6 py-4" style={{ borderTop: "1px solid oklch(0 0 0 / 10%)", background: "oklch(1 0 0)" }}>
          {!showReply ? (
            <button
              onClick={() => setShowReply(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
              style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)", border: "1px solid oklch(0 0 0 / 12%)" }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
              Reply to {email.from}
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="text-xs font-semibold mb-1" style={{ color: "oklch(0.35 0.02 240)" }}>
                Replying to {email.from} &lt;{email.fromEmail}&gt;
              </div>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Write your reply…`}
                rows={4}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(0 0 0 / 12%)", color: "oklch(0.12 0.04 240)" }}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReply}
                  disabled={replyMutation.isPending || !replyText.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, oklch(0.72 0.14 75), oklch(0.6 0.12 75))", color: "oklch(0.12 0.04 240)" }}
                >
                  {replyMutation.isPending ? (
                    <><svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Sending…</>
                  ) : (
                    <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>Send Reply</>
                  )}
                </button>
                <button
                  onClick={() => { setShowReply(false); setReplyText(""); }}
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)", border: "1px solid oklch(0 0 0 / 12%)" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Attachments Panel (unchanged) ───────────────────────────────────────────
function AttachmentsPanel() {
  const { data: attachments, isLoading } = trpc.email.attachments.useQuery({ limit: 100 });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <svg className="w-8 h-8 animate-spin" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <svg className="w-8 h-8" style={{ color: "oklch(0.35 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
        </svg>
        <p className="text-sm" style={{ color: "oklch(0.35 0.02 240)" }}>No attachments found</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-1">
        {(attachments as unknown as (EmailAttachment & { emailFrom: string; emailSubject: string; emailDate: number })[]).map((att) => (
          <a
            key={att.s3Key}
            href={att.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            download={att.filename}
            className="rounded-xl overflow-hidden flex flex-col hover:opacity-90 transition-opacity"
            style={{ background: "oklch(1 0 0)", border: "1px solid oklch(0 0 0 / 10%)" }}
          >
            {att.isImage ? (
              <div className="w-full flex items-center justify-center overflow-hidden" style={{ height: "120px", background: "oklch(0.94 0.007 240)" }}>
                <img src={att.s3Url} alt={att.filename} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-full flex items-center justify-center" style={{ height: "80px", background: "oklch(0.94 0.007 240)" }}>
                <svg className="w-8 h-8" style={{ color: "oklch(0.35 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
            )}
            <div className="p-2.5 flex flex-col gap-0.5">
              <p className="text-xs font-medium truncate" style={{ color: "oklch(0.12 0.04 240)" }}>{att.filename}</p>
              <p className="text-xs truncate" style={{ color: "oklch(0.35 0.02 240)" }}>{att.emailFrom}</p>
              <p className="text-xs" style={{ color: "oklch(0.45 0.02 240)" }}>{formatBytes(att.size)}</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function EmailInbox() {
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "unread" | "client" | "document" | "tax" | "invoice" | "payroll">("all");
  const [showCompose, setShowCompose] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [activeTab, setActiveTab] = useState<"inbox" | "attachments">("inbox");
  const [daysBack, setDaysBack] = useState<number>(3);
  const [search, setSearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  const isReadOnly = user?.role === "super_admin" || user?.role === "admin";
  const isBookkeeper = user?.role === "bookkeeper";

  const { data: clientStatuses } = trpc.clientStatus.listAll.useQuery(undefined, { enabled: isBookkeeper });

  const assignedClientNames = useMemo(() => {
    if (!isBookkeeper || !clientStatuses) return undefined;
    const initials = user?.name?.split(" ").map((w) => w[0]).join("").toUpperCase() ?? "";
    return (clientStatuses as Array<{ bookkeeperBankFeed: string; bookkeeperCoding: string; clientName: string }>)
      .filter((c) => c.bookkeeperBankFeed === initials || c.bookkeeperCoding === initials)
      .map((c) => c.clientName);
  }, [isBookkeeper, clientStatuses, user]);

  const { data: emails, isLoading, error, refetch } = trpc.email.list.useQuery(
    { limit: 200, search: search || undefined, clientNames: isBookkeeper ? (assignedClientNames ?? []) : undefined },
    { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000, refetchInterval: 5 * 60 * 1000, refetchOnWindowFocus: false, retry: 1 }
  );

  const { data: syncStatus, refetch: refetchStatus } = trpc.email.syncStatus.useQuery(
    { callerEmail: user?.email ?? "" },
    { refetchInterval: 30000 }
  );

  const syncMutation = trpc.email.sync.useMutation({
    onSuccess: () => { refetch(); refetchStatus(); },
  });

  const { data: clientEmails = [] } = trpc.email.clientEmails.useQuery();
  const { data: auditLog = [] } = trpc.email.auditLog.useQuery({ limit: 50 }, { enabled: showAudit });

  const filtered = useMemo(() => {
    if (!emails) return [];
    if (filter === "all") return emails as EmailItem[];
    if (filter === "unread") return (emails as EmailItem[]).filter((e) => e.unread);
    return (emails as EmailItem[]).filter((e) => e.tag === filter);
  }, [emails, filter]);

  const unreadCount = useMemo(() => (emails as EmailItem[] | undefined)?.filter((e) => e.unread).length ?? 0, [emails]);

  const selectedEmail = useMemo(
    () => (filtered as EmailItem[]).find((e) => e.messageId === selectedId) ?? null,
    [filtered, selectedId]
  );

  const handleSelect = useCallback((id: string) => {
    setSelectedId((prev) => (prev === id ? prev : id));
  }, []);

  const staffName = user?.name ?? "Staff";
  const staffEmail = user?.email ?? "";
  const staffRole = user?.role ?? "";

  return (
    <DashboardLayout noPadding>
      <div className="flex flex-col h-full min-h-0" style={{ padding: "0" }}>

        {/* ── Top bar + tabs (single compact row) ── */}
        <div className="flex items-center gap-2 px-4 flex-shrink-0"
          style={{ borderBottom: "1px solid oklch(0 0 0 / 10%)", background: "oklch(1 0 0)", minHeight: "44px" }}>
          {/* Left: title + unread */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <svg className="w-4 h-4" style={{ color: "oklch(0.35 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <span className="text-sm font-bold" style={{ color: "oklch(0.12 0.04 240)", fontFamily: "'Playfair Display', serif" }}>Email Inbox</span>
            {unreadCount > 0 && activeTab === "inbox" && (
              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                style={{ background: "oklch(0.72 0.14 75)", color: "oklch(0.12 0.04 240)" }}>
                {unreadCount}
              </span>
            )}
          </div>

          {/* Center: Inbox / Attachments tabs */}
          <div className="flex items-center gap-0 mx-2" style={{ borderLeft: "1px solid oklch(0 0 0 / 10%)", paddingLeft: "12px" }}>
            {([{ key: "inbox", label: "Inbox" }, { key: "attachments", label: "Attachments" }] as const).map(({ key, label }) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className="px-3 py-1.5 text-xs font-medium transition-all relative"
                style={{
                  color: activeTab === key ? "oklch(0.08 0.01 240)" : "oklch(0.45 0.02 240)",
                  borderBottom: activeTab === key ? "2px solid oklch(0.72 0.14 75)" : "2px solid transparent",
                  marginBottom: "-1px",
                }}>
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 ml-auto">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
              style={{ background: "oklch(0.94 0.007 240)", border: "1px solid oklch(0 0 0 / 10%)" }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: "oklch(0.55 0.15 145)" }} />
              <span className="text-sm" style={{ color: "oklch(0.22 0.03 240)" }}>apxadvisor@gmail.com</span>
            </div>

            {isReadOnly && activeTab === "inbox" && (
              <button onClick={() => setShowAudit((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{ background: showAudit ? "oklch(0.92 0.005 240 / 20%)" : "oklch(0.94 0.007 240)", color: "oklch(0.12 0.04 240)", border: "1px solid oklch(0 0 0 / 10%)" }}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
                Audit Log
              </button>
            )}

            {!isReadOnly && activeTab === "inbox" && (
              <button onClick={() => setShowCompose(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-all"
                style={{ background: "linear-gradient(135deg, oklch(0.72 0.14 75), oklch(0.6 0.12 75))", color: "oklch(0.12 0.04 240)" }}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
                New Email
              </button>
            )}

            {activeTab === "inbox" && (
              <select value={daysBack} onChange={(e) => setDaysBack(Number(e.target.value))}
                className="h-8 px-2 rounded-lg text-sm outline-none cursor-pointer"
                style={{ background: "oklch(0.94 0.007 240)", border: "1px solid oklch(0 0 0 / 10%)", color: "oklch(0.22 0.03 240)" }}>
                <option value={1}>Today</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            )}

            {activeTab === "inbox" && (
              <button onClick={() => refetch()}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5"
                style={{ color: "oklch(0.22 0.03 240)" }} title="Refresh">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>
            )}
          </div>
        </div>



        {/* ── Audit log ── */}
        {showAudit && activeTab === "inbox" && (
          <div className="flex-shrink-0 mx-6 mt-4 rounded-xl overflow-hidden"
            style={{ background: "oklch(1 0 0)", border: "1px solid oklch(0 0 0 / 10%)", maxHeight: "240px", overflowY: "auto" }}>
            <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid oklch(0 0 0 / 8%)" }}>
              <span className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)", fontFamily: "'Playfair Display', serif" }}>Email Audit Log</span>
              <span className="text-sm" style={{ color: "oklch(0.35 0.02 240)" }}>— last 50 actions</span>
            </div>
            {auditLog.length === 0 ? (
              <p className="text-sm text-center py-6" style={{ color: "oklch(0.35 0.02 240)" }}>No audit entries yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid oklch(0 0 0 / 8%)" }}>
                    {["Staff", "Action", "Subject", "Client", "Time"].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((row) => (
                    <tr key={row.id} style={{ borderBottom: "1px solid oklch(0 0 0 / 5%)" }}>
                      <td className="px-3 py-2" style={{ color: "oklch(0.12 0.04 240)" }}>{row.staffName}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold capitalize"
                          style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}>
                          {row.action}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate" style={{ color: "oklch(0.22 0.03 240)" }}>{row.subject}</td>
                      <td className="px-3 py-2" style={{ color: "oklch(0.22 0.03 240)" }}>{row.clientName ?? "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: "oklch(0.35 0.02 240)" }}>{new Date(row.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Inbox: two-panel split ── */}
        {activeTab === "inbox" && (
          <div className="flex flex-1 min-h-0">

            {/* LEFT: email list panel (40%) */}
            <div className="flex flex-col min-h-0 flex-shrink-0" style={{ width: "40%", borderRight: "1px solid oklch(0 0 0 / 10%)" }}>

              {/* Search + sync bar */}
              <div className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                style={{ borderBottom: "1px solid oklch(0 0 0 / 8%)", background: "oklch(0.97 0.005 85)" }}>
                <div className="relative flex-1">
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "oklch(0.45 0.02 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input type="text" placeholder="Search…" value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput); }}
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg text-sm outline-none"
                    style={{ background: "oklch(1 0 0)", border: "1px solid oklch(0 0 0 / 10%)", color: "oklch(0.12 0.04 240)" }} />
                </div>
                {search && (
                  <button onClick={() => { setSearch(""); setSearchInput(""); }}
                    className="px-2 py-1.5 rounded-lg text-xs"
                    style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)" }}>Clear</button>
                )}
                <button
                  onClick={() => syncMutation.mutate({ callerEmail: user?.email ?? "" })}
                  disabled={syncMutation.isPending}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ background: "oklch(0.94 0.007 240)", border: "1px solid oklch(0 0 0 / 10%)", color: "oklch(0.22 0.03 240)" }}>
                  <svg className={`w-3 h-3 ${syncMutation.isPending ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                  {syncMutation.isPending ? "Syncing…" : "Sync"}
                </button>
              </div>

              {/* Sync status */}
              {syncStatus && syncStatus.totalEmails > 0 && (
                <div className="px-3 py-1.5 flex-shrink-0 text-xs" style={{ color: "oklch(0.45 0.02 240)", background: "oklch(0.97 0.005 85)", borderBottom: "1px solid oklch(0 0 0 / 6%)" }}>
                  {syncStatus.totalEmails} emails · last sync {syncStatus.lastSyncedAt > 0 ? new Date(syncStatus.lastSyncedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "never"}
                </div>
              )}

              {/* Role banners */}
              {isBookkeeper && (
                <div className="flex items-center gap-2 px-3 py-2 flex-shrink-0 text-xs"
                  style={{ background: "oklch(0.94 0.018 220)", borderBottom: "1px solid oklch(0 0 0 / 8%)", color: "oklch(0.22 0.03 240)" }}>
                  Showing emails for your assigned clients only.
                </div>
              )}
              {isReadOnly && (
                <div className="flex items-center gap-2 px-3 py-2 flex-shrink-0 text-xs"
                  style={{ background: "oklch(0.94 0.018 220)", borderBottom: "1px solid oklch(0 0 0 / 8%)", color: "oklch(0.22 0.03 240)" }}>
                  Director view — read-only oversight.
                </div>
              )}

              {/* Filter tabs */}
              <div className="flex items-center gap-1 px-3 py-2 flex-shrink-0 overflow-x-auto"
                style={{ borderBottom: "1px solid oklch(0 0 0 / 8%)", background: "oklch(0.97 0.005 85)" }}>
                {(["all", "unread", "client", "document", "tax", "invoice", "payroll"] as const).map((f) => (
                  <button key={f} onClick={() => setFilter(f)}
                    className="flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium capitalize transition-all"
                    style={{
                      background: filter === f ? "oklch(0.72 0.14 75 / 15%)" : "transparent",
                      color: filter === f ? "oklch(0.12 0.04 240)" : "oklch(0.45 0.02 240)",
                      border: `1px solid ${filter === f ? "oklch(0.72 0.14 75 / 40%)" : "transparent"}`,
                    }}>
                    {f === "all" ? `All (${(emails as EmailItem[] | undefined)?.length ?? 0})` :
                     f === "unread" ? `Unread (${unreadCount})` : f}
                  </button>
                ))}
              </div>

              {/* Email list */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-40 gap-3">
                    <svg className="w-7 h-7 animate-spin" style={{ color: "oklch(0.35 0.02 240)" }} fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    <p className="text-xs" style={{ color: "oklch(0.45 0.02 240)" }}>Loading inbox…</p>
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center justify-center h-40 gap-2 p-4 text-center">
                    <p className="text-sm font-semibold" style={{ color: "oklch(0.12 0.04 240)" }}>Could not connect to Gmail</p>
                    <p className="text-xs" style={{ color: "oklch(0.35 0.02 240)" }}>Check GMAIL_APP_PASSWORD in Settings → Secrets.</p>
                    <button onClick={() => refetch()} className="px-3 py-1.5 rounded-lg text-xs font-medium mt-1"
                      style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)" }}>Retry</button>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 gap-2">
                    <p className="text-xs" style={{ color: "oklch(0.45 0.02 240)" }}>No emails match this filter</p>
                  </div>
                ) : (
                  filtered.map((email) => (
                    <EmailListRow
                      key={email.messageId}
                      email={email as EmailItem}
                      isSelected={selectedId === email.messageId}
                      onSelect={() => handleSelect(email.messageId)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* RIGHT: reading pane (60%) */}
            <div className="flex-1 min-h-0 min-w-0">
              <ReadingPane
                email={selectedEmail}
                isReadOnly={isReadOnly}
                staffName={staffName}
                staffEmail={staffEmail}
                staffRole={staffRole}
              />
            </div>
          </div>
        )}

        {/* ── Attachments tab ── */}
        {activeTab === "attachments" && (
          <div className="flex-1 min-h-0 p-6">
            <AttachmentsPanel />
          </div>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <ComposeModal
          onClose={() => setShowCompose(false)}
          staffName={staffName}
          staffEmail={staffEmail}
          staffRole={staffRole}
          clientEmails={clientEmails as { name: string; email: string | null }[]}
        />
      )}
    </DashboardLayout>
  );
}
